import {
  DsDialogService,
  DsDialogRef,
  DS_DIALOG_DATA,
  DsDialogConfig,
} from 'src/app/shared/components/ds-dialog/ds-dialog.component';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NotRespondingComponent } from './not-responding.component';

@Injectable({
  providedIn: 'root',
})
export class NotRespondingService {
  private _retry = new Subject<boolean>();
  private _cancel = new Subject<void>();
  private _retrying$ = new BehaviorSubject<boolean>(false);
  private _urlMap = new Map<string, boolean>();
  private _dialogRef: DsDialogRef<NotRespondingComponent> | null = null;

  /**
   * Whether the dialog close was triggered programmatically (retry success or recovery).
   * When false and dialog closes, it means the user dismissed it manually.
   */
  private _programmaticClose = false;

  /** Interceptor subscribes — emits when user clicks Retry */
  get retry(): Observable<boolean> {
    return this._retry.asObservable();
  }

  /** Interceptor subscribes — emits when user dismisses dialog */
  get cancel(): Observable<void> {
    return this._cancel.asObservable();
  }

  /** Component subscribes — true while request is retrying */
  get retrying$(): Observable<boolean> {
    return this._retrying$.asObservable();
  }

  constructor(private _dialog: DsDialogService) {}

  /**
   * Called by the dialog component when user clicks Retry.
   * Sets retrying state and notifies the interceptor.
   */
  setRetry(value: boolean): void {
    this._retrying$.next(true);
    this._retry.next(value);
  }

  /**
   * Called by the interceptor when a retried request fails again.
   * Resets the retrying state so the dialog button becomes active.
   */
  setRetryFailed(): void {
    this._retrying$.next(false);
  }

  openDialog(): void {
    if (this._dialogRef) {
      return;
    }
    this._programmaticClose = false;
    this._retrying$.next(false);

    this._dialogRef = this._dialog.open(NotRespondingComponent, {
      width: '500px',
      panelClass: 'dialog__wrapper',
      disableClose: false,
    });

    this._dialogRef.afterClosed().subscribe(() => {
      this._dialogRef = null;
      this._retrying$.next(false);
      if (!this._programmaticClose) {
        // User dismissed manually (X button) — cancel all pending retries
        this._urlMap.clear();
        this._cancel.next();
      }
      this._programmaticClose = false;
    });
  }

  /**
   * Programmatic close — used only when request succeeds or all URLs recover.
   * Never triggers cancel.
   */
  closeDialog(): void {
    if (!this._dialogRef) {
      return;
    }
    this._programmaticClose = true;
    this._dialogRef.close();
  }

  check(status: boolean, url: string): void {
    if (status === true) {
      this._urlMap.set(url, status);
      this.openDialog();
    } else if (status === false && this._urlMap.has(url)) {
      this._urlMap.delete(url);
    }

    if (this._urlMap.size === 0) {
      this.closeDialog();
    }
  }
}
